/**
 * AEGIS domain schemas — response validation, metacognition, confidence gate.
 * Merged from src/schemas/aegis.js, aegisMetacognition.js, confidenceGateDecision.js.
 */
import { z } from 'zod';
import { TenantIdSchema, IsoDateSchema } from './_primitives.js';

// ── AEGIS response schemas ────────────────────────────────────────────────────

export const HaikuExtractionSchema = z.object({
  intent: z.enum([
    'shipment_status_inquiry',
    'document_request',
    'payment_inquiry',
    'exception_report',
    'quote_request',
    'general',
  ]),
  poNumbers:     z.array(z.string()).default([]),
  loadIds:       z.array(z.string()).default([]),
  dockTimes:     z.array(z.string()).default([]),
  exceptionType: z.string().nullable().optional(),
  sentiment:     z.enum(['positive', 'neutral', 'negative']),
  urgency:       z.enum(['critical', 'high', 'normal', 'low']),
  partyRole:     z.enum(['customer', 'carrier', 'broker', 'vendor', 'internal', 'unknown']),
  customerName:  z.string().nullable().optional(),
}).passthrough();

export const ReplyDraftSchema = z.object({
  draftText:  z.string().min(1, 'draftText must not be empty'),
  confidence: z.number().min(0).max(1),
  decisionId: z.string().nullable().optional(),
  mocked:     z.boolean().optional(),
  provider:   z.string().optional(),
}).passthrough();

export const SonnetDraftSchema = ReplyDraftSchema.extend({
  draftId:           z.string().optional(),
  dataSourceSummary: z.array(z.any()).optional(),
  missingDataFlags:  z.array(z.string()).optional(),
  groundingTrail:    z.array(z.any()).optional(),
  routing: z.object({
    queue:   z.string().optional(),
    urgency: z.string().optional(),
  }).passthrough().optional(),
  queue:                z.string().optional(),
  urgency:              z.string().optional(),
  governanceDecisionId: z.string().nullable().optional(),
  auditRef:             z.string().nullable().optional(),
});

/** @typedef {import('zod').infer<typeof HaikuExtractionSchema>} HaikuExtraction */
/** @typedef {import('zod').infer<typeof ReplyDraftSchema>} ReplyDraft */
/** @typedef {import('zod').infer<typeof SonnetDraftSchema>} SonnetDraft */

// ── Metacognition schemas (COG-META-1) ────────────────────────────────────────

export const META_D_DECISION_TYPES = Object.freeze({
  FRAUD_ESCALATION:      'fraud-escalation',
  CREDIT_DECISION:       'credit-decision',
  EXCEPTION_RESOLUTION:  'exception-resolution',
  MARGIN_ANOMALY:        'margin-anomaly',
  REPLY_DRAFTING:        'reply-drafting',
  GENERAL_SUMMARIZATION: 'general-summarization',
});

export const META_D_THRESHOLDS = Object.freeze({
  'fraud-escalation':      1.5,
  'credit-decision':       1.3,
  'exception-resolution':  1.0,
  'margin-anomaly':        0.8,
  'reply-drafting':        0.7,
  'general-summarization': 0.5,
});

export const META_D_DEFAULT_THRESHOLD = 0.8;

export const DISCRIMINATION_CLASSES = Object.freeze({
  HIGH:             'high',
  MEDIUM:           'medium',
  LOW:              'low',
  INSUFFICIENT_DATA:'insufficient-data',
});

export const OUTCOME_TYPES = Object.freeze({
  CONFIRMED:  'confirmed',
  REJECTED:   'rejected',
  OVERRIDDEN: 'overridden',
});

export const ReasoningSignalsSchema = z.object({
  tokenEntropy:       z.number().min(0).nullable().optional(),
  selfConsistency:    z.number().min(0).max(1),
  groundingCoverage:  z.number().min(0).max(1),
  groundingVariance:  z.number().min(0),
  reSampleCount:      z.number().int().min(1),
});

export const GroundTruthRefSchema = z.object({
  outcomeType:     z.enum(Object.values(OUTCOME_TYPES)),
  observedAt:      IsoDateSchema,
  attributionPath: z.string().min(1),
});

export const MetacognitionTraceSchema = z.object({
  id:                   z.string().min(1),
  tenantId:             TenantIdSchema,
  requestId:            z.string().min(1),
  correlationId:        z.string().nullable().optional(),
  agentId:              z.string().min(1),
  decisionType:         z.string().min(1),
  modelVersion:         z.string().min(1),
  reasoningSignals:     ReasoningSignalsSchema,
  predictedConfidence:  z.number().min(0).max(1),
  adjustedConfidence:   z.number().min(0).max(1),
  groundTruthRef:       GroundTruthRefSchema.nullable().optional(),
  createdAt:            IsoDateSchema,
  updatedAt:            IsoDateSchema,
  createdBy:            z.string(),
  updatedBy:            z.string(),
  isDeleted:            z.boolean(),
  version:              z.number().int().nonnegative(),
  source:               z.string(),
}).passthrough();

export const MetacognitionScoresSchema = z.object({
  id:                   z.string().min(1),
  tenantId:             TenantIdSchema,
  agentId:              z.string().min(1),
  decisionType:         z.string().min(1),
  windowStart:          IsoDateSchema,
  windowEnd:            IsoDateSchema,
  sampleSize:           z.number().int().nonnegative(),
  metaD:                z.number(),
  confidenceMean:       z.number().min(0).max(1),
  accuracyObserved:     z.number().min(0).max(1),
  calibration:          z.number().min(0),
  discriminationClass:  z.enum(Object.values(DISCRIMINATION_CLASSES)),
  lastUpdated:          IsoDateSchema,
  version:              z.number().int().nonnegative(),
  createdAt:            IsoDateSchema,
  updatedAt:            IsoDateSchema,
  createdBy:            z.string(),
  updatedBy:            z.string(),
  isDeleted:            z.boolean(),
  source:               z.string(),
}).passthrough();

/** @typedef {import('zod').infer<typeof MetacognitionTraceSchema>} MetacognitionTrace */
/** @typedef {import('zod').infer<typeof MetacognitionScoresSchema>} MetacognitionScores */

// ── Confidence gate decision (COG-META-1 §3.4) ───────────────────────────────

export const GATE_VERDICT = Object.freeze({
  AUTO_SEND:           'auto_send',
  AMBER_BAND:          'amber_band',
  HITL_REQUIRED:       'hitl_required',
  ABSTAIN:             'abstain',
  CALIBRATION_PENDING: 'calibration_pending',
});

export const GATE_VERDICTS = Object.values(GATE_VERDICT);

export const ConfidenceGateDecisionSchema = z.object({
  gateVersion:   z.string().default('COG-META-1-v1'),
  decisionId:    z.string(),
  tenantId:      z.string(),
  agentId:       z.string().nullable().default(null),
  decisionType:  z.string(),
  evaluatedAt:   z.string(),
  confidence:    z.number().min(0).max(1).nullable(),
  metaD:         z.number().nullable(),
  sampleSize:    z.number().int().nonnegative(),
  metaDFloor:    z.number().nullable(),
  stakesLevel:   z.enum(['routine', 'high', 'critical']).default('routine'),
  verdict:       z.enum(GATE_VERDICTS),
  abstentionReason: z.string().nullable().default(null),
  autoSendEligible: z.boolean(),
  amberBandLow:  z.number().nullable().default(null),
  amberBandHigh: z.number().nullable().default(null),
  flag:          z.string().nullable().default(null),
}).passthrough();

export const CONFIDENCE_GATE_DEFAULTS = Object.freeze({
  AUTO_SEND_FLOOR:        0.85,
  ABSTAIN_CONFIDENCE:     0.50,
  ABSTAIN_META_D:         0.40,
  MIN_SAMPLE_SIZE:        20,
  AMBER_HIGH_META_D_LOW:  0.75,
  AMBER_HIGH_META_D_HIGH: 0.85,
  AMBER_LOW_META_D_LOW:   0.65,
  AMBER_LOW_META_D_HIGH:  0.90,
});
